// Bloque "Sugerencias de la IA" de la pestaña Correos (formato nuevo).
//
// Cada noche la IA revisa los correos sin presupuesto; cuando no está segura,
// deja aquí la sugerencia para que una persona diga Sí (se enlaza el hilo) o
// No (no se vuelve a proponer este presupuesto para ese correo).
import React, { useCallback, useEffect, useState } from 'react';
import { vinculoIaAPI } from '../../api/api';

const TEXTO_CONFIANZA = { alta: 'Confianza alta', media: 'Confianza media', baja: 'Confianza baja' };
const ESTILO_CONFIANZA = {
  alta: 'bg-[#E3F4EC] text-[#11704F]',
  media: 'bg-[#FDF0DC] text-[#92400E]',
  baja: 'bg-gray-100 text-gray-600',
};

const formatFecha = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  const f = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`);
  if (Number.isNaN(f.getTime())) return '';
  return f.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const SugerenciasCorreosIA = ({ presupuestoId, onCambio }) => {
  const [sugerencias, setSugerencias] = useState([]);
  const [procesando, setProcesando] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    if (!presupuestoId) return;
    try {
      const { data } = await vinculoIaAPI.sugeridos(presupuestoId);
      setSugerencias(Array.isArray(data?.correos) ? data.correos : []);
    } catch (err) {
      console.error('Error cargando sugerencias de la IA:', err);
      setSugerencias([]);
    }
  }, [presupuestoId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const responder = async (correo, acepta) => {
    setProcesando(correo.id);
    setError(null);
    try {
      if (acepta) await vinculoIaAPI.aceptar(correo.id, presupuestoId);
      else await vinculoIaAPI.rechazar(correo.id);
      setSugerencias((prev) => prev.filter((c) => c.id !== correo.id));
      if (acepta && onCambio) onCambio();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo guardar la respuesta');
    } finally {
      setProcesando(null);
    }
  };

  if (sugerencias.length === 0) return null;

  return (
    <div className="border border-[#E1E6E3] rounded-lg bg-[#F7FAF8] p-3" data-testid="sugerencias-correos-ia">
      <div className="mb-2">
        <h4 className="text-sm font-bold text-[#16221D]">Sugerencias de la IA</h4>
        <p className="text-xs text-[#5B6B64]">
          Correos que podrían ser de este evento. Si dices Sí, se enlaza la conversación completa.
        </p>
      </div>
      {error && <p className="text-xs text-red-700 mb-2">{error}</p>}
      <ul className="space-y-2">
        {sugerencias.map((correo) => {
          const vi = correo.vinculo_ia || {};
          const quien = correo.es_enviado
            ? `Para: ${(correo.destinatarios || []).join(', ')}`
            : (correo.remitente_nombre || correo.remitente || '');
          const ocupado = procesando === correo.id;
          return (
            <li key={correo.id} className="bg-white border border-[#E1E6E3] rounded-lg p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-[#5B6B64]">
                    <span>{formatFecha(correo.fecha)}</span>
                    {vi.confianza && (
                      <span className={`px-1.5 py-0.5 rounded font-semibold ${ESTILO_CONFIANZA[vi.confianza] || ESTILO_CONFIANZA.baja}`}>
                        {TEXTO_CONFIANZA[vi.confianza] || vi.confianza}
                      </span>
                    )}
                    {correo.mensajes_en_hilo > 1 && <span>{correo.mensajes_en_hilo} mensajes en la conversación</span>}
                  </div>
                  <p className="text-sm font-semibold text-[#16221D] truncate mt-0.5">{correo.asunto || '(sin asunto)'}</p>
                  <p className="text-xs text-gray-600 truncate">{quien}</p>
                  {vi.motivo && <p className="text-xs text-[#16221D] mt-1">Motivo: {vi.motivo}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    disabled={ocupado}
                    onClick={() => responder(correo, true)}
                    className="h-[30px] px-3 rounded-lg border text-[13px] font-bold bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90 disabled:opacity-50"
                  >
                    Sí
                  </button>
                  <button
                    type="button"
                    disabled={ocupado}
                    onClick={() => responder(correo, false)}
                    className="h-[30px] px-3 rounded-lg border text-[13px] font-bold border-[#E1E6E3] text-[#16221D] bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    No
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SugerenciasCorreosIA;
